import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, UserPlus, UserRound, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ImageUploadField } from "@/components/ImageUploadField";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", photo: "", address: "", lat: "", lng: "", phone: "", email: "", website: "", facebook: "", instagram: "", youtube: "", hours: "", description: "", color: "#2563EB" };

export const AdminMosques = ({ assoc, user }) => {
  const { t } = useLang();
  const isAssocAdmin = !user || user.role === "association_admin" || user.role === "super_admin";
  const [mosques, setMosques] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [managers, setManagers] = useState({});
  const [mgrMosque, setMgrMosque] = useState(null);
  const [mgrForm, setMgrForm] = useState({ name: "", email: "", password: "" });

  const loadManagers = useCallback(() => {
    if (!isAssocAdmin) return;
    api.get(`/associations/${assoc.slug}/members`).then(({ data }) => {
      const map = {};
      data.filter((u) => u.role === "mosque_admin").forEach((u) => {
        (u.mosque_ids || []).forEach((id) => { map[id] = [...(map[id] || []), u.name]; });
      });
      setManagers(map);
    }).catch(() => {});
  }, [assoc.slug, isAssocAdmin]);

  useEffect(() => { loadManagers(); }, [loadManagers]);

  const designate = async () => {
    try {
      await api.post(`/associations/${assoc.slug}/admins`, { ...mgrForm, role: "mosque_admin", mosque_ids: [mgrMosque.id] });
      toast.success(t("manager_designated"));
      setMgrMosque(null);
      setMgrForm({ name: "", email: "", password: "" });
      loadManagers();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const load = useCallback(() => {
    api.get(`/associations/${assoc.slug}/mosques`).then(({ data }) => {
      setMosques(isAssocAdmin ? data : data.filter((m) => (user?.mosque_ids || []).includes(m.id)));
    });
  }, [assoc.slug, isAssocAdmin, user]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const payload = { ...form };
    payload.lat = form.lat !== "" && form.lat !== null ? parseFloat(form.lat) : null;
    payload.lng = form.lng !== "" && form.lng !== null ? parseFloat(form.lng) : null;
    delete payload.id; delete payload.association_id; delete payload.created_at;
    try {
      if (editId) await api.put(`/associations/${assoc.slug}/mosques/${editId}`, payload);
      else await api.post(`/associations/${assoc.slug}/mosques`, payload);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/associations/${assoc.slug}/mosques/${id}`);
    load();
  };

  const copyLink = (m) => {
    const url = `${window.location.origin}/${assoc.slug}/mosques/${m.id}`;
    const done = () => toast.success(t("link_copied"));
    const fallback = () => {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); done(); } catch { toast.error(url); }
      document.body.removeChild(ta);
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(fallback);
    else fallback();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("mosques")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          {isAssocAdmin && (
            <DialogTrigger asChild>
              <Button data-testid="add-mosque-btn" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} className="rounded-full active:scale-95">
                <Plus className="h-4 w-4 mr-2" /> {t("add_mosque")}
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editId ? t("edit") : t("add_mosque")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>{t("name")}</Label><Input data-testid="mosque-form-name" value={form.name} onChange={set("name")} /></div>
              <div className="space-y-1.5"><Label>{t("address")}</Label><Input data-testid="mosque-form-address" value={form.address} onChange={set("address")} /></div>
              <div className="space-y-1.5">
                <Label>{t("gps_coords")}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" step="any" placeholder="Latitude (48.8566)" data-testid="mosque-form-lat" value={form.lat ?? ""} onChange={set("lat")} />
                  <Input type="number" step="any" placeholder="Longitude (2.3522)" data-testid="mosque-form-lng" value={form.lng ?? ""} onChange={set("lng")} />
                </div>
                <p className="text-[11px] text-muted-foreground">{t("gps_hint")}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("phone")}</Label><Input data-testid="mosque-form-phone" value={form.phone} onChange={set("phone")} /></div>
                <div className="space-y-1.5"><Label>{t("email")}</Label><Input data-testid="mosque-form-email" value={form.email} onChange={set("email")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("website")}</Label><Input data-testid="mosque-form-website" value={form.website} onChange={set("website")} /></div>
                <div className="space-y-1.5">
                  <Label>{t("color")}</Label>
                  <Input type="color" data-testid="mosque-form-color" value={form.color} onChange={set("color")} className="h-9 p-1" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("image_url")}</Label>
                <ImageUploadField slug={assoc.slug} value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} testId="mosque-form-photo" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("social_links")}</Label>
                <Input placeholder="Facebook (https://facebook.com/...)" data-testid="mosque-form-facebook" value={form.facebook} onChange={set("facebook")} />
                <Input placeholder="Instagram (https://instagram.com/...)" data-testid="mosque-form-instagram" value={form.instagram} onChange={set("instagram")} />
                <Input placeholder="YouTube (https://youtube.com/...)" data-testid="mosque-form-youtube" value={form.youtube} onChange={set("youtube")} />
              </div>
              <div className="space-y-1.5"><Label>{t("hours")}</Label><Input data-testid="mosque-form-hours" value={form.hours} onChange={set("hours")} /></div>
              <div className="space-y-1.5"><Label>{t("description")}</Label><Textarea rows={3} data-testid="mosque-form-description" value={form.description} onChange={set("description")} /></div>
              <Button data-testid="mosque-form-submit" onClick={submit} disabled={!form.name} className="w-full rounded-full active:scale-95">{t("save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-mosques-grid">
        {mosques.map((m) => (
          <div key={m.id} className="border border-border rounded-lg bg-card overflow-hidden">
            {m.photo && <img src={m.photo} alt={m.name} className="w-full h-32 object-cover" />}
            <div className="p-4">
              <p className="font-heading font-bold flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: m.color }} /> {m.name}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{m.address}</p>
              {managers[m.id]?.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5" data-testid="mosque-manager-names">
                  <UserRound className="h-3 w-3 text-accent" /> {t("mosque_manager")} : {managers[m.id].join(", ")}
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="outline" size="sm" data-testid="copy-mosque-link-btn"
                  onClick={() => copyLink(m)}
                  className="rounded-full h-7 text-xs">
                  <Link2 className="h-3 w-3 mr-1.5" /> {t("copy_link")}
                </Button>
                <Button variant="outline" size="sm" data-testid="edit-mosque-btn" onClick={() => { setForm({ ...EMPTY, ...m }); setEditId(m.id); setOpen(true); }} className="rounded-full h-7 text-xs">
                  <Pencil className="h-3 w-3 mr-1.5" /> {t("edit")}
                </Button>
                {isAssocAdmin && (
                  <>
                    <Button variant="outline" size="sm" data-testid="designate-manager-btn" onClick={() => setMgrMosque(m)} className="rounded-full h-7 text-xs">
                      <UserPlus className="h-3 w-3 mr-1.5" /> {t("mosque_manager")}
                    </Button>
                    <Button variant="outline" size="sm" data-testid="delete-mosque-btn" onClick={() => remove(m.id)} className="rounded-full h-7 text-xs text-destructive">
                      <Trash2 className="h-3 w-3 mr-1.5" /> {t("delete")}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!mgrMosque} onOpenChange={(v) => !v && setMgrMosque(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("designate_manager")} — {mgrMosque?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{t("designate_manager_hint")}</p>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{t("name")}</Label><Input data-testid="mgr-form-name" value={mgrForm.name} onChange={(e) => setMgrForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("email")}</Label><Input type="email" data-testid="mgr-form-email" value={mgrForm.email} onChange={(e) => setMgrForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>{t("password")}</Label><Input type="password" data-testid="mgr-form-password" value={mgrForm.password} onChange={(e) => setMgrForm((f) => ({ ...f, password: e.target.value }))} /></div>
            <Button data-testid="mgr-form-submit" onClick={designate} disabled={!mgrForm.name || !mgrForm.email || mgrForm.password.length < 6} className="w-full rounded-full active:scale-95">
              {t("designate_manager")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
